import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mail, Calendar, MessageSquare, FileText } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Spinner, Empty, Avatar, RoleBadge, Time } from "@/forum/ui";
import { ThreadRow } from "./CategoryPage";

export default function ProfilePage() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    if (user === null) return;
    setData(null);
    api.get(`/forum/users/${username}`).then((r) => setData(r.data)).catch((e) => setErr(errorText(e)));
  }, [username, user]);

  if (err) return <Empty text={err} testId="profile-error" />;
  if (!data) return <Spinner />;
  const u = data.user;

  return (
    <div className="space-y-6" data-testid="profile-page">
      <Panel className="relative overflow-hidden p-6 sm:p-10">
        <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${u.avatar_color}44, transparent 60%)` }} />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
          <Avatar user={u} size="xl" />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-3xl font-bold" data-testid="profile-username">{u.username}</h1>
              <RoleBadge role={u.role} label={u.role_label} />
              {u.banned && <span className="rounded-full border border-red-500/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-red-300">Kitiltva</span>}
            </div>
            <p className="mt-2 max-w-xl text-sm text-white/60" data-testid="profile-bio">{u.bio || "Ez a felhasználó még nem írt magáról."}</p>
            <div className="mt-4 flex flex-wrap gap-4 font-mono text-[11px] text-white/40">
              <span className="inline-flex items-center gap-1"><Calendar size={12} />Csatlakozott: {new Date(u.created_at).toLocaleDateString("hu-HU")}</span>
              <span className="inline-flex items-center gap-1"><FileText size={12} />{data.thread_count} téma</span>
              <span className="inline-flex items-center gap-1"><MessageSquare size={12} />{u.post_count} hozzászólás</span>
              <span>Utoljára aktív: <Time iso={u.last_seen} /></span>
            </div>
          </div>
          {user && user.id !== u.id && (
            <Link to={`/forum/messages/${u.username}`} className="btn-glow inline-flex items-center gap-2 self-start rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black" data-testid="profile-message-button"><Mail size={14} />Üzenet</Link>
          )}
          {user && user.id === u.id && (
            <Link to="/forum/settings" className="inline-flex items-center gap-2 self-start rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold hover:bg-white/5" data-testid="profile-edit-button">Profil szerkesztése</Link>
          )}
        </div>
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">Legutóbbi témák</h2>
          <Panel className="overflow-hidden">
            {data.threads.length ? <div className="divide-y divide-white/[0.05]">{data.threads.map((t) => <ThreadRow key={t.id} t={t} />)}</div> : <Empty text="Még nem nyitott témát" testId="profile-no-threads" />}
          </Panel>
        </section>
        <section className="space-y-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">Legutóbbi hozzászólások</h2>
          <div className="space-y-3">
            {data.posts.length ? data.posts.map((p) => (
              <Panel key={p.id} className="p-4">
                <Link to={`/forum/t/${p.thread_id}`} className="font-display text-sm font-semibold hover:text-[var(--glow)]">{p.thread_title}</Link>
                <p className="mt-1 line-clamp-2 text-sm text-white/55">{p.content}</p>
                <Time iso={p.created_at} className="mt-2 block" />
              </Panel>
            )) : <Empty text="Még nem szólt hozzá" testId="profile-no-posts" />}
          </div>
        </section>
      </div>
    </div>
  );
}
